import {
  FRAME_DURATION,
  lastCompleteFrameTime,
  snapToExportFrame,
} from '@/lib/timeline/exportFrameTiming';

// Scene edits the assistant makes on the clip timeline (scenes back to back,
// 0 = clip start). Scenes are the editor's natural timeline: source ranges in
// playback order. Every new edge snaps to an export frame, a scene is at least
// one frame long and a clip keeps at least one scene.

export interface Scene {
  id: number;
  startTime: number;
  endTime: number;
  title?: string;
}

export class SceneEditError extends Error {
  constructor(readonly code: string, readonly detail?: string) {
    super(code);
  }
}

const EPSILON = 1e-7;
const MAX_EXTEND_SECONDS = 120;

export function clipDurationOf(scenes: readonly Scene[]): number {
  return scenes.reduce((total, scene) => total + Math.max(0, scene.endTime - scene.startTime), 0);
}

/** Where a clip time falls: the scene index and the source time there. */
export function locateClipTime(
  scenes: readonly Scene[],
  time: number,
): { index: number; sourceTime: number } | null {
  if (!Number.isFinite(time) || time < 0) return null;
  let offset = 0;
  for (let index = 0; index < scenes.length; index += 1) {
    const scene = scenes[index];
    const length = scene.endTime - scene.startTime;
    if (time < offset + length) return { index, sourceTime: scene.startTime + (time - offset) };
    offset += length;
  }
  const last = scenes[scenes.length - 1];
  return last && Math.abs(time - offset) < EPSILON
    ? { index: scenes.length - 1, sourceTime: last.endTime }
    : null;
}

function normalizeRanges(ranges: ReadonlyArray<{ start: number; end: number }>, total: number) {
  if (!Array.isArray(ranges) || ranges.length === 0) throw new SceneEditError('invalid_ranges');
  const clamped = ranges.map((range) => {
    const start = Number(range?.start);
    const end = Number(range?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      throw new SceneEditError('invalid_ranges', 'Each range needs start < end');
    }
    return { start: Math.max(0, start), end: Math.min(total, end) };
  }).filter((range) => range.end - range.start > EPSILON)
    .sort((a, b) => a.start - b.start);
  if (!clamped.length) throw new SceneEditError('outside_clip');
  const merged: Array<{ start: number; end: number }> = [];
  clamped.forEach((range) => {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push({ ...range });
  });
  return merged;
}

/** Remove clip-time ranges. A cut inside a scene splits it; the first piece
 *  keeps the scene id, later pieces get `nextId()`. */
export function cutClipRanges(
  scenes: Scene[],
  ranges: ReadonlyArray<{ start: number; end: number }>,
  nextId: () => number,
): Scene[] {
  const cuts = normalizeRanges(ranges, clipDurationOf(scenes));
  const out: Scene[] = [];
  let offset = 0;
  scenes.forEach((scene) => {
    const length = scene.endTime - scene.startTime;
    const sceneStart = offset;
    const sceneEnd = offset + length;
    offset = sceneEnd;
    // Kept clip-time pieces of this scene.
    let pieces = [{ start: sceneStart, end: sceneEnd }];
    cuts.forEach((cut) => {
      pieces = pieces.flatMap((piece) => {
        if (cut.end <= piece.start || cut.start >= piece.end) return [piece];
        return [
          { start: piece.start, end: Math.max(piece.start, cut.start) },
          { start: Math.min(piece.end, cut.end), end: piece.end },
        ].filter((part) => part.end - part.start > EPSILON);
      });
    });
    let first = true;
    pieces.forEach((piece) => {
      const startTime = snapToExportFrame(scene.startTime + (piece.start - sceneStart));
      const endTime = snapToExportFrame(scene.startTime + (piece.end - sceneStart));
      if (endTime - startTime < FRAME_DURATION - EPSILON) return;
      out.push({ ...scene, id: first ? scene.id : nextId(), startTime, endTime });
      first = false;
    });
  });
  if (!out.length) throw new SceneEditError('would_remove_all', 'Keep at least part of the clip');
  return out;
}

/** New clip-time start and/or end for the whole clip. */
export function trimClip(scenes: Scene[], edges: { start?: number; end?: number }): Scene[] {
  const total = clipDurationOf(scenes);
  const start = edges.start === undefined ? undefined : Number(edges.start);
  const end = edges.end === undefined ? undefined : Number(edges.end);
  if (start === undefined && end === undefined) throw new SceneEditError('nothing_to_change');
  if ((start !== undefined && !Number.isFinite(start)) || (end !== undefined && !Number.isFinite(end))) {
    throw new SceneEditError('invalid_ranges');
  }
  if (start !== undefined && end !== undefined && start >= end) {
    throw new SceneEditError('invalid_ranges', 'start must be before end');
  }
  const ranges: Array<{ start: number; end: number }> = [];
  if (start !== undefined && start > 0) ranges.push({ start: 0, end: start });
  if (end !== undefined && end < total) ranges.push({ start: end, end: total });
  if (!ranges.length) throw new SceneEditError('nothing_to_change');
  return cutClipRanges(scenes, ranges, () => {
    throw new SceneEditError('invalid_ranges');
  });
}

/** Grow the clip into the source: before the first scene, after the last. */
export function extendClip(
  scenes: Scene[],
  edges: { before?: number; after?: number },
  sourceDuration: number,
): Scene[] {
  const amount = (value: number | undefined) => (
    value === undefined || !Number.isFinite(Number(value))
      ? 0
      : Math.min(MAX_EXTEND_SECONDS, Math.max(0, Number(value)))
  );
  const before = amount(edges.before);
  const after = amount(edges.after);
  if (!before && !after) throw new SceneEditError('nothing_to_change');
  const next = scenes.map((scene) => ({ ...scene }));
  const first = next[0];
  const last = next[next.length - 1];
  const sourceEnd = lastCompleteFrameTime(Math.max(0, sourceDuration));
  let changed = false;
  if (before && first) {
    const startTime = snapToExportFrame(Math.max(0, first.startTime - before));
    if (startTime < first.startTime - EPSILON) {
      first.startTime = startTime;
      changed = true;
    }
  }
  if (after && last) {
    const endTime = Math.min(sourceEnd, snapToExportFrame(last.endTime + after));
    if (endTime > last.endTime + EPSILON) {
      last.endTime = endTime;
      changed = true;
    }
  }
  if (!changed) throw new SceneEditError('at_source_edge', 'The clip already reaches the start/end of the video');
  return next;
}

/** Split the scene under a clip time into two. */
export function splitSceneAt(scenes: Scene[], time: number, nextId: () => number): Scene[] {
  const at = locateClipTime(scenes, Number(time));
  if (!at) throw new SceneEditError('outside_clip');
  const scene = scenes[at.index];
  const cut = snapToExportFrame(at.sourceTime);
  if (cut < snapToExportFrame(scene.startTime) + FRAME_DURATION - EPSILON
    || cut > snapToExportFrame(scene.endTime) - FRAME_DURATION + EPSILON) {
    throw new SceneEditError('too_close_to_edge');
  }
  const next = [...scenes];
  next.splice(at.index, 1, { ...scene, endTime: cut }, { ...scene, id: nextId(), startTime: cut });
  return next;
}

export function removeScene(scenes: Scene[], index: number): Scene[] {
  return removeScenes(scenes, [index]);
}

/** Remove several scenes at once, by their indexes in `scenes` (so removing
 *  [1, 3] removes the scenes the model saw as 1 and 3). */
export function removeScenes(scenes: Scene[], indexes: readonly number[]): Scene[] {
  const wanted = new Set(indexes);
  const valid = wanted.size > 0
    && [...wanted].every((index) => Number.isInteger(index) && index >= 0 && index < scenes.length);
  if (!valid) throw new SceneEditError('invalid_scene', `indexes must be 0-${scenes.length - 1}`);
  if (wanted.size >= scenes.length) throw new SceneEditError('would_remove_all', 'A clip keeps at least one scene');
  return scenes.filter((_, position) => !wanted.has(position));
}

/** `order` lists every current scene index once, in the new playback order. */
export function reorderScenes(scenes: Scene[], order: readonly number[]): Scene[] {
  const valid = Array.isArray(order)
    && order.length === scenes.length
    && new Set(order).size === scenes.length
    && order.every((index) => Number.isInteger(index) && index >= 0 && index < scenes.length);
  if (!valid) throw new SceneEditError('invalid_order', `order must list 0-${scenes.length - 1} once each`);
  return order.map((index) => scenes[index]);
}
